import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

const API_BASE = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'http://localhost:3001/api';

// Helper function to get auth token
export const getAuthToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('No authentication token available');
  }

  return session.access_token;
};

// Helper function to make authenticated API calls
const authenticatedFetch = async (url, options = {}) => {
  const token = await getAuthToken();
  
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    // Handle insufficient tokens specifically
    if (response.status === 402) {
      const error = await response.json().catch(() => ({ error: 'Insufficient tokens' }));
      throw new Error(error.error || 'Insufficient tokens');
    }
    
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

// Token Management
export const tokenService = {
  // Test authentication
  testAuth: async () => {
    console.log('🧪 Testing authentication...');
    return authenticatedFetch('/subscription/test-auth');
  },

  // Use tokens for a conversation or action
  useTokens: async (amount, purpose = 'conversation') => {
    return authenticatedFetch('/subscription/use-tokens', {
      method: 'POST',
      body: JSON.stringify({ amount, purpose }),
    });
  },

  // Get user's current token info
  getTokenInfo: async () => {
    return authenticatedFetch('/subscription/token-info');
  },

  // Check if user has enough tokens before an action
  checkTokens: async (tokensNeeded = 1) => {
    return authenticatedFetch('/subscription/check-tokens', {
      method: 'POST',
      body: JSON.stringify({ tokensNeeded }),
    });
  },
};

// Subscription Management
export const subscriptionService = {
  // Create yearly subscription
  createYearlySubscription: async () => {
    const result = await authenticatedFetch('/subscription/create-yearly-subscription', {
      method: 'POST',
    });
    
    // Redirect to Stripe Checkout
    if (result.sessionUrl) {
      window.location.href = result.sessionUrl;
    }
    
    return result;
  },

  // Create monthly subscription
  createMonthlySubscription: async () => {
    const result = await authenticatedFetch('/subscription/create-monthly-subscription', {
      method: 'POST',
    });
    
    // Redirect to Stripe Checkout
    if (result.sessionUrl) {
      window.location.href = result.sessionUrl;
    }
    
    return result;
  },

  // Purchase tokens (one-time)
  createTokenPurchase: async (packageType) => {
    const result = await authenticatedFetch('/subscription/purchase-tokens', {
      method: 'POST',
      body: JSON.stringify({ packageType }),
    });
    
    // Redirect to Stripe Checkout
    if (result.sessionUrl) {
      window.location.href = result.sessionUrl;
    }
    
    return result;
  },

  // Cancel subscription
  cancelSubscription: async () => {
    return authenticatedFetch('/subscription/cancel-subscription', {
      method: 'POST',
    });
  },

  // Reactivate canceled subscription
  reactivateSubscription: async () => {
    return authenticatedFetch('/subscription/reactivate-subscription', {
      method: 'POST',
    });
  },

  // Get subscription details
  getSubscriptionDetails: async () => {
    return authenticatedFetch('/subscription/subscription-details');
  },

  // Update payment method
  updatePaymentMethod: async () => {
    return authenticatedFetch('/subscription/update-payment-method', {
      method: 'POST',
    });
  },
};

// Stripe Payment Integration
export const paymentService = {
  // Process payment with Stripe (for subscriptions)
  processSubscriptionPayment: async (clientSecret, paymentMethod) => {
    const { loadStripe } = await import('@stripe/stripe-js');
    const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

    if (!stripe) {
      throw new Error('Stripe failed to load');
    }

    const result = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        payment_method: paymentMethod,
        return_url: `${window.location.origin}/subscription/success`,
      },
    });

    if (result.error) {
      throw new Error(result.error.message);
    }

    return result;
  },

  // Process one-time token purchase
  processTokenPurchase: async (clientSecret, paymentMethod) => {
    const { loadStripe } = await import('@stripe/stripe-js');
    const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

    if (!stripe) {
      throw new Error('Stripe failed to load');
    }

    const result = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        payment_method: paymentMethod,
        return_url: `${window.location.origin}/tokens/success`,
      },
    });

    if (result.error) {
      throw new Error(result.error.message);
    }

    return result;
  },
};

// Subscription Status Helpers
export const subscriptionHelpers = {
  // Check if user can perform an action based on tokens/subscription
  canPerformAction: (tokenInfo, tokensNeeded = 1) => {
    if (!tokenInfo) return false;
    
    return {
      allowed: tokenInfo.tokenBalance >= tokensNeeded,
      reason: tokenInfo.tokenBalance < tokensNeeded ? 'insufficient_tokens' : null,
      tokenBalance: tokenInfo.tokenBalance,
      subscriptionStatus: tokenInfo.subscriptionStatus,
    };
  },

  // Get user-friendly subscription status
  getSubscriptionStatusText: (subscriptionDetails) => {
    if (!subscriptionDetails) return 'No subscription';

    switch (subscriptionDetails.subscriptionStatus) {
      case 'active':
        return `${subscriptionDetails.subscriptionType} Subscription`;
      case 'canceled':
        return 'Subscription Canceled';
      case 'inactive':
        return 'Subscription Inactive';
      default:
        return 'No Subscription';
    }
  },
};

// React Hook for subscription state
export const useSubscription = () => {
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [subscription, tokens] = await Promise.all([
        subscriptionService.getSubscriptionDetails(),
        tokenService.getTokenInfo(),
      ]);

      setSubscriptionDetails(subscription);
      setTokenInfo(tokens);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSubscription();
  }, []);

  return {
    subscriptionDetails,
    tokenInfo,
    loading,
    error,
    refreshSubscription,
    canPerformAction: (tokensNeeded) => 
      subscriptionHelpers.canPerformAction(tokenInfo, tokensNeeded),
  };
};

// React Hook for subscription status (alias for useSubscription with better naming)
export const useSubscriptionStatus = () => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [subscription, tokens] = await Promise.all([
        subscriptionService.getSubscriptionDetails(),
        tokenService.getTokenInfo(),
      ]);

      // Transform subscription details into a more convenient format
      // Apply cancelled subscription logic - treat as active until subscription_ends_at
      let effectiveStatus = subscription?.subscriptionStatus || 'inactive';
      let isActive = effectiveStatus === 'active';
      
      if (subscription?.subscriptionEndsAt) {
        const endDate = new Date(subscription.subscriptionEndsAt);
        if (endDate < new Date()) {
          effectiveStatus = 'expired';
          isActive = false;
        } else if (subscription?.subscriptionStatus === 'cancelled' || subscription?.subscriptionStatus === 'canceled') {
          // Cancelled subscription is still active until end date (handle both spellings)
          effectiveStatus = 'active';
          isActive = true;
        }
      }
      
      setSubscriptionStatus({
        isActive: isActive,
        status: effectiveStatus,
        plan: subscription?.subscriptionType, // 'yearly' or 'monthly'
        endsAt: subscription?.subscriptionEndsAt,
        cancelAtPeriodEnd: subscription?.stripeSubscription?.cancelAtPeriodEnd,
        rawSubscription: subscription // Keep full data for debugging
      });
      
      setTokenInfo({
        balance: tokens?.tokenBalance || 0,
        ...tokens
      });
    } catch (err) {
      setError(err.message);
      console.error('Error fetching subscription status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return {
    subscriptionStatus,
    tokenInfo,
    loading,
    error,
    refresh,
  };
};

const subscriptionServiceExport = {
  tokenService,
  subscriptionService,
  paymentService,
  subscriptionHelpers,
  useSubscription,
  useSubscriptionStatus,
};

export default subscriptionServiceExport;
